import { TranslationsContext } from '@teliaee/telia-translate';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { ButtonNavigation, IButtonNavigationItem, LargeSearch, Separator, Text, TextField } from 'telia-front-react';
import MovieRentalCatalogStore, { ALL_CATEGORIES_ID, ContentType, Movie, MovieCategoriesParams } from '../stores/movierental/MovieRentalCatalogStore';
import { FetchStatus } from '../util/FetchStatus';
import MovieRentalCatalog from './MovieRentalCatalog';
import TvCategories from './TvCategories';
import TvRentalCatalog from './TvRentalCatalog';

interface TvSearchProps {
  moviesCategoryId?: string;
  seriesCategoryId?: string;
  applicationId?: string;
  rowsPerPage?: number;
  type?: string;
  vodType?: string;
  packetIds?: string[];
  isFreeOfCharge?: boolean;
  movieRentalCatalogStore?: MovieRentalCatalogStore;
}

const TvSearch = observer(
  class TvSearch extends React.Component<TvSearchProps> {
    static contextType = TranslationsContext;
    declare context: React.ContextType<typeof TranslationsContext>;
    public rowsPerPage = 3;
    public itemsPerRow = 6;

    public searchInput: string = null;
    public selectedCategory: string = this.getRootCategoryId();
    public topCategory: string = '';
    public page: number = 1;
    public lengthError: boolean = false;
    public searched: boolean = false;

    constructor(props: TvSearchProps) {
      super(props);
      makeObservable(this, {
        searchInput: observable,
        selectedCategory: observable,
        topCategory: observable,
        page: observable,
        lengthError: observable,
        searched: observable,
        resetPagination: action,
        changeCategory: action,
        changeTopCategory: action,
        invalidSearchInput: action,
      });
      this.topCategory = props.type ?? ContentType.ASSETS;
      if (props.rowsPerPage) {
        this.rowsPerPage = props.rowsPerPage;
      }
    }

    public getMovieCategoriesParams(): MovieCategoriesParams {
      const { vodType, packetIds, applicationId } = this.props;
      return {
        rootCategoryId: this.getRootCategoryId(),
        type: this.topCategory,
        vodType,
        packetIds,
        applicationId,
      };
    }

    public resetPagination() {
      this.page = 1;
    }

    public changeCategory(category: string) {
      this.selectedCategory = category;
      this.updateSearchResults();
      this.resetPagination();
    }

    public changeTopCategory(category: string) {
      this.topCategory = category as ContentType;
      this.selectedCategory = this.getRootCategoryId();
      this.updateSearchResults();
      this.resetPagination();
    }

    public invalidSearchInput() {
      const { movieRentalCatalogStore } = this.props;

      this.lengthError = false;
      if (!this.searchInput) {
        this.searched = false;
        movieRentalCatalogStore.clearSearchResults();
        return true;
      }
      if (this.searchInput.length < 2) {
        this.lengthError = true;
        this.searched = true;
        movieRentalCatalogStore.clearSearchResults();
        return true;
      }
      return false;
    }

    public updateSearchResults = async () => {
      const { loadMovieCategories, getCategories, loadSearchResults, searchInputChanged, topCategoryChanged } = this.props.movieRentalCatalogStore;

      if (this.invalidSearchInput() || (!searchInputChanged(this.searchInput) && !topCategoryChanged(this.topCategory) && this.noSearchResults())) {
        return;
      }
      runInAction(() => {
        this.searched = true;
      });
      await loadMovieCategories(this.getMovieCategoriesParams());
      const categoryIds = (getCategories(this.getMovieCategoriesParams()) || []).map((category) => `${category.id}`).concat([this.getRootCategoryId()]);

      if (!categoryIds.includes(`${this.selectedCategory}`)) {
        runInAction(() => {
          this.selectedCategory = this.getRootCategoryId();
        });
      }

      await loadSearchResults(this.searchInput, this.selectedCategory, this.topCategory, categoryIds, this.props.packetIds);
      if (!this.categoryIds()?.includes(`${this.selectedCategory}`)) {
        runInAction(() => {
          this.selectedCategory = this.getRootCategoryId();
        });
      }
    };

    public getSearchResults() {
      const results = this.props.movieRentalCatalogStore.searchResultsByTopCategoryAndCategory?.get(this.topCategory)?.get(`${this.selectedCategory}`);

      return (
        results || {
          status: FetchStatus.UNINITIALIZED,
          results: [],
        }
      );
    }

    public noSearchResults = () => {
      const resultsHolder = this.props.movieRentalCatalogStore.searchResultsByTopCategoryAndCategory?.get(this.topCategory)?.get(`${this.selectedCategory}`);
      return resultsHolder?.status === FetchStatus.FETCHED && (!resultsHolder?.results || !resultsHolder?.results.length);
    };

    public categoryIds() {
      const searchResults = this.props.movieRentalCatalogStore.searchResultsByTopCategoryAndCategory;
      const allCategories = Array.from(searchResults?.get(this.topCategory)?.keys() || []);
      const categories = allCategories.filter((categoryId) => searchResults?.get(this.topCategory)?.get(categoryId)?.results.length > 0);
      return categories.length > 0 ? categories.concat([this.getRootCategoryId()]) : undefined;
    }

    public getRootCategoryId(): string {
      const { moviesCategoryId, seriesCategoryId } = this.props;
      let rootCategoryId: string;
      switch (this.topCategory) {
        case ContentType.ASSETS:
          rootCategoryId = moviesCategoryId || ALL_CATEGORIES_ID;
          break;
        case ContentType.SERIES:
          rootCategoryId = seriesCategoryId || '1006';
          break;
        default:
          rootCategoryId = moviesCategoryId || seriesCategoryId || ALL_CATEGORIES_ID;
      }
      return rootCategoryId;
    }

    public pageResults(): Movie[] {
      const itemsPerPage = this.itemsPerRow * this.rowsPerPage;
      return this.getSearchResults().results.slice((this.page - 1) * itemsPerPage, this.page * itemsPerPage);
    }

    public getMoviesPerPage() {
      return this.itemsPerRow * this.rowsPerPage;
    }

    public submit = (e: React.FormEvent) => {
      e.preventDefault();
      this.updateSearchResults();
    };

    public topCategoryOnClick = (e: React.MouseEvent, topCategory: ContentType) => {
      e.preventDefault();
      this.changeTopCategory(topCategory);
    };

    public getContentTypeButtonProps(contentType: ContentType): IButtonNavigationItem {
      const translate = this.context.translate;
      return {
        id: contentType,
        label: contentType === ContentType.ASSETS ? translate('tvSearch.films') : translate('tvSearch.series'),
        current: this.topCategory === contentType,
        url: '#',
        onClick: (event) => {
          this.topCategoryOnClick(event, contentType);
        },
      };
    }

    private onSearchChange = (val: string) => {
      runInAction(() => {
        this.searchInput = val;
      });
    };

    public render() {
      const translate = this.context.translate;
      const { movieRentalCatalogStore, vodType, packetIds, isFreeOfCharge, moviesCategoryId, seriesCategoryId } = this.props;
      return (
        <Text className="text-center">
          <LargeSearch onSubmit={this.submit} id="tv-search">
            <TextField
              id="search-input"
              label={this.topCategory === ContentType.SERIES ? translate('tvSearch.searchLabel.series') : translate('tvSearch.searchLabel')}
              onChange={this.onSearchChange}
              onClear={() => {
                document.getElementById('search-input').focus();
                document.getElementById('search-input').blur();
              }}
              isClearable={true}
            />
          </LargeSearch>
          {!!moviesCategoryId && !!seriesCategoryId && (
            <>
              <Separator spacing="small" color="invisible" />
              <ButtonNavigation items={[this.getContentTypeButtonProps(ContentType.ASSETS), this.getContentTypeButtonProps(ContentType.SERIES)]} />
            </>
          )}
          <Separator spacing="small" color="invisible" />
          <TvCategories
            categoryIds={this.categoryIds()}
            categoryId={this.selectedCategory}
            {...this.props}
            rootCategoryId={this.getRootCategoryId()}
            type={this.topCategory}
            vodType={undefined}
            searchInput={this.searchInput}
            onCategoryChange={(category: string) => this.changeCategory(category)}
            isLoading={movieRentalCatalogStore.isFetchingSearchResults(this.topCategory)}
          />
          {this.lengthError && (
            <>
              <Separator spacing="small" color="invisible" />
              {translate('tvSearch.lengthError')}
            </>
          )}
          {this.searched ? (
            <TvRentalCatalog
              rootCategoryId={this.getRootCategoryId()}
              movies={this.pageResults()}
              moviesPerPage={this.getMoviesPerPage()}
              isFetching={this.getSearchResults().status !== FetchStatus.FETCHED && this.searched && !this.lengthError}
              isUninitialized={this.getSearchResults().status === FetchStatus.UNINITIALIZED}
              totalCount={this.getSearchResults().results.length}
              onPageChange={(page) => (this.page = page)}
            />
          ) : (
            <MovieRentalCatalog
              vodType={vodType}
              packetIds={packetIds}
              isFreeOfCharge={isFreeOfCharge}
              type={this.topCategory as ContentType}
              rootCategoryId={this.getRootCategoryId()}
              selectedCategoryId={this.selectedCategory}
              rowsPerPage={this.rowsPerPage}
            />
          )}
        </Text>
      );
    }
  },
);

export default inject('movieRentalCatalogStore')(TvSearch);
